import { TOO_MANY_REQUESTS_STATUS, TooManyRequestsError } from './rateLimit.ts';

const NOT_FOUND_STATUS = 404;

/** HTTP 404. Same message as before; the type and `status` let callers tell it apart. */
export class NotFoundError extends Error {
  readonly status = NOT_FOUND_STATUS;

  constructor(message: string) {
    super(message);
    this.name = 'NotFoundError';
  }
}

/** Duck-typed on `status` too, like `isTooManyRequestsError`. */
export const isNotFoundError = (error: unknown): boolean =>
  error instanceof NotFoundError ||
  (typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    (error as { status: unknown }).status === NOT_FOUND_STATUS);

/**
 * Handles errors in HTTP response objects and returns appropriate results or throws errors based on the status code.
 *
 * @param {Response} response - The HTTP response object to evaluate.
 * @param {string} [identifier] - Optional identifier representing the resource related to the response (used in error messages for context).
 * @param {boolean} [returnJsonResult=true] - Flag indicating whether to return the JSON result of the response when successful. Defaults to true.
 * @param {boolean} returnStringResult - Flag indicating whether to return the text result of the response when successful. Defaults to false. If returnJsonResult is true, this is ignored.
 * @returns {Promise<T|boolean>} If the response is successful (HTTP 2xx, same range as {@link Response#ok}).
 * @throws {Error} Throws an error with an appropriate message for HTTP status codes 400, 401, 404, 409, 429, 500, or other unexpected statuses.
 */
export function handleResponseErrors<T>(
  response: Response,
  identifier?: string,
  returnJsonResult?: boolean,
  returnStringResult?: boolean
): Promise<T>;
export function handleResponseErrors(
  response: Response,
  identifier?: string,
  returnJsonResult?: boolean,
  returnStringResult?: boolean
): Promise<boolean>;
export async function handleResponseErrors<T>(
  response: Response,
  identifier?: string,
  returnJsonResult: boolean = true,
  returnStringResult: boolean = false
): Promise<T | string | boolean | null> {
  if (response.status === 204) {
    // If status is 204 there is no response to deserialize
    return null;
  }
  if (response.ok) {
    if (returnJsonResult) return response.json();
    if (returnStringResult) return response.text();
    return response.ok;
  }
  const errorText = await response.text();
  switch (response.status) {
    case 400: {
      const detail =
        errorText.trim().length > 0
          ? errorText
          : 'Invalid or incomplete data. Please check and try again.';
      throw new Error(`Bad Request: ${detail}`);
    }
    case 401:
      throw new Error('Unauthorized: Please log in to continue.');
    case NOT_FOUND_STATUS:
      throw new NotFoundError(`Not found: ${identifier} does not exist. Details: ${errorText}`);
    case 409:
      throw new Error(
        `Conflict: ${identifier} already exists or there is a conflict. Details: ${errorText}`
      );
    case TOO_MANY_REQUESTS_STATUS:
      throw new TooManyRequestsError();
    case 500:
      console.error(errorText);
      throw new Error(`Server error: Something went wrong(500). Details: ${errorText}`);
    default: {
      console.error(errorText);
      throw new Error(`An unexpected error occurred. ${errorText}.`);
    }
  }
}
